import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Input,
  Listbox,
  ListboxItem,
  ListboxSection,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { WorkflowOutput } from "@webuild-ai/admin/models/components";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { logger } from "@/telemetry";

const SYSTEM_WORKFLOW_IDS = new Set(["chat_agent", "workflow_generator"]);

interface PlaygroundWorkflowSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (workflowId: string | null) => void;
}

const PlaygroundWorkflowSelector: React.FC<PlaygroundWorkflowSelectorProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation();
  const { adminClient } = useWebuildAPIClient();
  const [workflows, setWorkflows] = useState<WorkflowOutput[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isOpen && adminClient) {
      const fetchWorkflows = async () => {
        setIsLoading(true);
        try {
          const result = await adminClient.workflows.listWorkflows({});

          setWorkflows(result);
        } catch (error) {
          logger.error("Failed to fetch workflows", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchWorkflows();
    }
  }, [isOpen, adminClient]);

  const filteredWorkflows = workflows.filter(
    (wf) =>
      !SYSTEM_WORKFLOW_IDS.has(wf.id) &&
      (wf.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wf.description?.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const handleSelect = (workflowId: string | null) => {
    onSelect(workflowId);
    onClose();
    setSearchQuery("");
  };

  return (
    <Modal isOpen={isOpen} size="lg" onOpenChange={(open) => !open && onClose()}>
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h3>{t("Select Workflow")}</h3>
          <p className="text-xs text-default-500 font-normal">
            {t("Choose a workflow to load or start a new one")}
          </p>
        </ModalHeader>
        <ModalBody className="pb-6">
          <Input
            placeholder={t("Search workflows...")}
            size="sm"
            startContent={
              <Icon className="w-4 h-4 text-default-400" icon="solar:magnifer-linear" />
            }
            value={searchQuery}
            onValueChange={setSearchQuery}
          />

          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="text-center py-8 text-default-400">
                <Icon
                  className="w-6 h-6 mx-auto mb-2 animate-spin"
                  icon="solar:refresh-circle-linear"
                />
                <p className="text-sm">{t("Loading workflows...")}</p>
              </div>
            ) : (
              <Listbox
                aria-label={t("Workflow list")}
                emptyContent={t("No workflows found")}
                onAction={(key) => handleSelect(key === "new" ? null : String(key))}
              >
                <ListboxSection title="">
                  <ListboxItem
                    key="new"
                    className="mb-2"
                    startContent={
                      <Icon className="w-5 h-5 text-primary" icon="solar:add-circle-linear" />
                    }
                    textValue={t("New Workflow")}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-primary">{t("New Workflow")}</span>
                      <span className="text-xs text-default-400">
                        {t("Start with an empty canvas")}
                      </span>
                    </div>
                  </ListboxItem>
                </ListboxSection>
                <ListboxSection title={t("Workflows")}>
                  {filteredWorkflows.map((wf) => (
                    <ListboxItem
                      key={wf.id}
                      startContent={
                        <Icon className="w-5 h-5 text-default-500" icon="solar:widget-2-outline" />
                      }
                      textValue={wf.name}
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{wf.name}</span>
                        {wf.description && (
                          <span className="text-xs text-default-400 line-clamp-1">
                            {wf.description}
                          </span>
                        )}
                      </div>
                    </ListboxItem>
                  ))}
                </ListboxSection>
              </Listbox>
            )}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PlaygroundWorkflowSelector;
